import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, Divider, Chip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayIcon from '@mui/icons-material/Replay';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import JsBarcode from 'jsbarcode';
import { useTranslation } from 'react-i18next';
import packingService, { LabelData } from '../../services/packingService';
import { cargoService } from '../../services/cargoService';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import { formatCustomerAddress } from '../../shared/formatCustomerAddress';

interface PackingLabelPrintProps {
  orderId: string;
  onComplete: () => void;
  onPrintDone: () => void;
  onBack?: () => void;
}

const PackingLabelPrint: React.FC<PackingLabelPrintProps> = ({ orderId, onComplete, onPrintDone, onBack }) => {
  const { t } = useTranslation('warehouse');
  const [label, setLabel] = useState<LabelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [printed, setPrinted] = useState(false);
  const [cargoCreating, setCargoCreating] = useState(false);
  const [cargoCreated, setCargoCreated] = useState(false);
  const [cargoTrackingNumber, setCargoTrackingNumber] = useState<string | null>(null);
  const barcodeRef = useRef<HTMLCanvasElement>(null);
  const barcodeContainerRef = useRef<HTMLDivElement>(null);
  const { notification, notify, clear } = useWmsNotification();

  // Bazi e-ticaret entegrasyonlarinda adres ham JSON string olarak geliyor.
  // Etikette JSON gozukmesin diye okunur metne ceviriyoruz; ham metin ise olduğu gibi kalır.
  const formattedReceiverAddress = useMemo(
    () => (label?.receiverAddress ? formatCustomerAddress(label.receiverAddress) : ''),
    [label?.receiverAddress]
  );

  // Website (kendi sitemiz / Tacreo) siparisi mi? Pazaryeri siparisleri (trendyol/hepsiburada)
  // kendi kargo etiketini kullanir; MNG yalnizca website icin tetiklenir.
  const isWebsite = useMemo(() => {
    const src = (label?.marketplaceSource || '').toLowerCase();
    return src === '' || src === 'website';
  }, [label?.marketplaceSource]);

  useEffect(() => {
    (async () => {
      try {
        const data = await packingService.getLabel(orderId);
        setLabel(data);
      } catch {
        notify({ type: 'error', message: t('screens.packing.labelPrint.loadError') });
      }
      setLoading(false);
    })();
  }, [orderId, notify, t]);

  useEffect(() => {
    if (label && barcodeRef.current) {
      try {
        const containerWidth = barcodeContainerRef.current?.clientWidth || 320;
        const barcodeModuleWidth = containerWidth < 340 ? 1.2 : 1.6;
        JsBarcode(barcodeRef.current, label.barcodeData, {
          format: 'CODE128',
          width: barcodeModuleWidth,
          height: 56,
          displayValue: true,
          fontSize: 12,
          margin: 0,
          textMargin: 2,
          background: '#fff', lineColor: '#000',
        });
        barcodeRef.current.style.display = 'block';
        barcodeRef.current.style.maxWidth = '100%';
        barcodeRef.current.style.height = 'auto';
        barcodeRef.current.style.margin = '0 auto';
      } catch { /* invalid barcode */ }
    }
  }, [label]);

  const handleCreateShipment = useCallback(async () => {
    if (!orderId) return;
    setCargoCreating(true);
    try {
      const result = await cargoService.createShipment(orderId);
      if (result.success) {
        setCargoCreated(true);
        setCargoTrackingNumber(result.trackingNumber || null);
        notify({ type: 'success', message: t('screens.packing.labelPrint.cargoCreated', { tracking: result.trackingNumber || '-' }) });
        if (label) {
          setLabel({ ...label, trackingNumber: result.trackingNumber || label.trackingNumber, shippingMethod: result.message || label.shippingMethod });
        }
      } else {
        notify({ type: 'error', message: result.message || t('screens.packing.labelPrint.cargoCreateFailed') });
      }
    } catch (e: any) {
      const msg = e.response?.data?.message || e.response?.data?.Message || e.message || t('screens.packing.labelPrint.cargoCreateError');
      notify({ type: 'error', message: msg });
    } finally {
      setCargoCreating(false);
    }
  }, [orderId, label, notify, t]);

  // Barkodu React onizleme canvas'indan bagimsiz uret; boylece MNG olusturduktan hemen
  // sonra takip nolu barkodla yazdirabiliriz (state guncellemesini beklemeden).
  const generateBarcodeDataUrl = useCallback((value: string): string => {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, value, {
        format: 'CODE128', width: 1.6, height: 56, displayValue: true,
        fontSize: 12, margin: 0, textMargin: 2, background: '#fff', lineColor: '#000',
      });
      return canvas.toDataURL('image/png');
    } catch {
      return '';
    }
  }, []);

  const doPrint = useCallback((lp: LabelData) => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) {
      notify({ type: 'warning', message: t('screens.packing.labelPrint.popupBlocked') });
      setPrinted(true);
      return;
    }

    const barcodeDataUrl = generateBarcodeDataUrl(lp.barcodeData);
    const rcvAddress = lp.receiverAddress ? formatCustomerAddress(lp.receiverAddress) : '';

    printWindow.document.write(`<!DOCTYPE html><html><head><title>${t('screens.packing.labelPrint.printWindowCargoTitle')}</title>
      <style>
        body { margin: 0; padding: 16px; font-family: Arial, sans-serif; font-size: 13px; }
        .label { border: 2px solid #000; padding: 12px; max-width: 350px; margin: auto; }
        .row { display: flex; justify-content: space-between; margin: 4px 0; }
        .bold { font-weight: 700; }
        hr { border: none; border-top: 1px dashed #000; margin: 8px 0; }
        .barcode { text-align: center; margin-top: 12px; }
        .barcode img { max-width: 100%; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <div class="label">
        <div class="row"><span class="bold" style="font-size:16px">${lp.orderNumber}</span><span>${t('screens.packing.labelPrint.package', { number: lp.packageNumber, total: lp.totalPackages })}</span></div>
        <hr/>
        <div class="row"><span class="bold">${t('screens.packing.labelPrint.sender')}</span></div>
        <div class="row"><span>${lp.senderName || t('screens.packing.labelPrint.company')}</span></div>
        ${lp.senderPhone ? `<div class="row"><span>${t('screens.packing.labelPrint.phone', { value: lp.senderPhone })}</span></div>` : ''}
        ${lp.senderAddress ? `<div class="row"><span>${lp.senderAddress}</span></div>` : ''}
        <hr/>
        <div class="row"><span class="bold">${t('screens.packing.labelPrint.receiver')}</span></div>
        <div class="row"><span>${lp.receiverName}</span></div>
        ${lp.receiverPhone ? `<div class="row"><span>${t('screens.packing.labelPrint.phone', { value: lp.receiverPhone })}</span></div>` : ''}
        ${rcvAddress ? `<div class="row"><span>${rcvAddress}</span></div>` : ''}
        <hr/>
        ${lp.shippingMethod ? `<div class="row"><span class="bold">${t('screens.packing.labelPrint.cargo')}</span><span>${lp.shippingMethod}</span></div>` : ''}
        ${lp.trackingNumber ? `<div class="row"><span class="bold">${t('screens.packing.labelPrint.trackingNo')}</span><span>${lp.trackingNumber}</span></div>` : ''}
        <div class="row"><span class="bold">${t('screens.packing.labelPrint.itemCountLabel')}</span><span>${lp.itemCount}</span></div>
        <div class="barcode"><img src="${barcodeDataUrl}" alt="barcode" /></div>
      </div>
      <script>window.onload=function(){window.print();}<\/script>
      </body></html>`);
    printWindow.document.close();
    setPrinted(true);
    notify({ type: 'success', message: t('screens.packing.labelPrint.printedToast') });
  }, [generateBarcodeDataUrl, notify, t]);

  const handlePrint = useCallback(async () => {
    if (!label) return;

    try {
      await packingService.completePacking(orderId);
    } catch {
      notify({ type: 'error', message: t('screens.packing.labelPrint.completeError') });
      return;
    }

    let labelForPrint = label;

    // Website siparisi: yazdirmadan once gercek MNG gonderisi olustur, takip nosunu etikete bas.
    if (isWebsite && !cargoCreated && !label.trackingNumber) {
      setCargoCreating(true);
      try {
        const result = await cargoService.createShipment(orderId);
        if (!result.success) {
          notify({ type: 'error', message: result.message || t('screens.packing.labelPrint.mngError') });
          return; // etiket basma; "Yazdır" butonu kalir, tekrar denenebilir
        }
        const tracking = result.trackingNumber || undefined;
        const updated: LabelData = {
          ...label,
          trackingNumber: tracking || label.trackingNumber,
          shippingMethod: 'MNG Kargo',
          barcodeData: tracking || label.barcodeData,
        };
        setLabel(updated);
        setCargoCreated(true);
        setCargoTrackingNumber(tracking || null);
        labelForPrint = updated;
        notify({ type: 'success', message: t('screens.packing.labelPrint.cargoCreated', { tracking: tracking || '-' }) });
      } catch (e: any) {
        const msg = e.response?.data?.message || e.response?.data?.Message || e.message || t('screens.packing.labelPrint.mngError');
        notify({ type: 'error', message: msg });
        return; // etiket basma
      } finally {
        setCargoCreating(false);
      }
    }

    doPrint(labelForPrint);
  }, [label, orderId, isWebsite, cargoCreated, notify, t, doPrint]);

  const handleReprint = useCallback(() => {
    if (!label || !barcodeRef.current) return;
    const barcodeDataUrl = barcodeRef.current?.toDataURL('image/png') || '';
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${t('screens.packing.labelPrint.printWindowLabelTitle')}</title>
      <style>body{margin:0;padding:16px;font-family:Arial;font-size:13px}.label{border:2px solid #000;padding:12px;max-width:350px;margin:auto}.row{display:flex;justify-content:space-between;margin:4px 0}.bold{font-weight:700}hr{border:none;border-top:1px dashed #000;margin:8px 0}.barcode{text-align:center;margin-top:12px}.barcode img{max-width:100%}@media print{body{padding:0}}</style></head><body>
      <div class="label">
        <div class="row"><span class="bold" style="font-size:16px">${label.orderNumber}</span></div>
        <div class="barcode"><img src="${barcodeDataUrl}" alt="barcode" /></div>
        <div class="row"><span>${t('screens.packing.labelPrint.receiverInline', { name: label.receiverName })}</span></div>
      </div><script>window.onload=function(){window.print();}<\/script></body></html>`);
    printWindow.document.close();
  }, [label, t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!label) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{t('screens.packing.labelPrint.loadError')}</Typography>
        <Button onClick={onComplete} sx={{ mt: 2 }}>{t('screens.packing.labelPrint.backButton')}</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
      <WmsNotification notification={notification} onClose={clear} />

      <Typography variant="h5" sx={{ fontWeight: 700, textAlign: 'center' }}>{t('screens.packing.labelPrint.title')}</Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Paper sx={{
          p: { xs: 1.5, sm: 3 },
          bgcolor: '#fff',
          color: '#000',
          borderRadius: 2,
          border: '2px solid #333',
          maxWidth: { xs: '100%', sm: 400 },
          width: '100%',
          overflow: 'hidden',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: { xs: 15, sm: 18 }, minWidth: 0, wordBreak: 'break-all' }}
            >
              {label.orderNumber}
            </Typography>
            <Typography variant="body2" sx={{ ml: 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {t('screens.packing.labelPrint.package', { number: label.packageNumber, total: label.totalPackages })}
            </Typography>
          </Box>

          <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

          <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('screens.packing.labelPrint.sender')}</Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{label.senderName || t('screens.packing.labelPrint.company')}</Typography>
          {label.senderPhone && <Typography variant="body2">{t('screens.packing.labelPrint.phone', { value: label.senderPhone })}</Typography>}
          {label.senderAddress && (
            <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
              {formatCustomerAddress(label.senderAddress)}
            </Typography>
          )}

          <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

          <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('screens.packing.labelPrint.receiver')}</Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{label.receiverName}</Typography>
          {label.receiverPhone && <Typography variant="body2">{t('screens.packing.labelPrint.phone', { value: label.receiverPhone })}</Typography>}
          {formattedReceiverAddress && (
            <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
              {formattedReceiverAddress}
            </Typography>
          )}

          <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

          {label.shippingMethod && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('screens.packing.labelPrint.cargo')}</Typography>
              <Typography variant="body2">{label.shippingMethod}</Typography>
            </Box>
          )}
          {label.trackingNumber && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('screens.packing.labelPrint.trackingNo')}</Typography>
              <Typography variant="body2">{label.trackingNumber}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('screens.packing.labelPrint.itemCountLabel')}</Typography>
            <Typography variant="body2">{label.itemCount}</Typography>
          </Box>

          <Box ref={barcodeContainerRef} sx={{ textAlign: 'center', mt: 2, width: '100%', overflow: 'hidden' }}>
            <canvas ref={barcodeRef} />
          </Box>
        </Paper>
      </Box>

      {/* Cargo section — yalnizca website (Tacreo) siparislerinde MNG kargo aksiyonlari */}
      {printed && isWebsite && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          {!cargoCreated ? (
            <Button variant="contained" size="large" color="warning"
              startIcon={cargoCreating ? <CircularProgress size={20} /> : <LocalShippingIcon />}
              onClick={handleCreateShipment} disabled={cargoCreating}
              sx={{ minWidth: 220, fontSize: 15, fontWeight: 700 }}>
              {cargoCreating ? t('screens.packing.labelPrint.creating') : t('screens.packing.labelPrint.createCargo')}
            </Button>
          ) : (
            <Chip
              icon={<LocalShippingIcon />}
              label={t('screens.packing.labelPrint.trackingChip', { tracking: cargoTrackingNumber || label.trackingNumber || '-' })}
              color="success" variant="outlined"
              sx={{ fontSize: 15, py: 2.5, px: 1 }}
            />
          )}
        </Box>
      )}

      {/* Pazaryeri siparisi: kargo etiketi pazaryerinin kendi arayuzunden alinir */}
      {printed && !isWebsite && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 420 }}>
            {t('screens.packing.labelPrint.marketplaceOwnCargo')}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
        {!printed ? (
          <>
            {onBack && (
              <Button variant="outlined" size="large" startIcon={<ArrowBackIcon />}
                onClick={onBack} sx={{ minWidth: 160 }}>
                {t('screens.packing.common.back')}
              </Button>
            )}
            <Button variant="contained" size="large" startIcon={<PrintIcon />}
              onClick={handlePrint} sx={{ minWidth: 200, fontSize: 16, fontWeight: 700 }}>
              {t('screens.packing.labelPrint.print')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" size="large" startIcon={<ReplayIcon />}
              onClick={handleReprint} sx={{ minWidth: 180 }}>
              {t('screens.packing.labelPrint.reprint')}
            </Button>
            <Button variant="contained" size="large" startIcon={<NavigateNextIcon />}
              onClick={onPrintDone} color="success"
              sx={{ minWidth: 200, fontSize: 16, fontWeight: 700 }}>
              {t('screens.packing.labelPrint.nextPackage')}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PackingLabelPrint;
